import { ActionIcon, Button, Group, Input, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { closeAllModals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { ErrorBoundary } from '@sentry/react';
import cx from 'clsx';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate, useParams } from 'react-router-dom';

import { useDataSourcesModal, AddFAQsParams, useFAQsList, useFAQsMethods } from '@mygpt/app';

import { account } from '@mygpt/app';
const { ContentWrap } = account;
import { SvgFile, SvgSearch1, SvgTrash } from '@/components/Common/icon';
import { ErrorContent } from '@/components/ErrorContent';
import { useFaqsImportHome, useFaqsImportMode } from '@/components/Faqs/hooks';
import { SvgCollapse, SvgExpand } from '@/components/Faqs/icon';
import { RichEditor } from '@/components/TiptapRichEditor/compat';
import { useStepValue } from '@/hooks/useStepStore';

import classes from './import-filter.module.css';

type FaqsItem = Omit<AddFAQsParams, 'datasetId'> & { id: string };

interface FaqsImportFilterItemProps {
  loading: boolean;
  item: FaqsItem;
  index: number;
  onDelete: (item: FaqsItem) => void;
  onUpdate: (item: FaqsItem) => void;
}

const FaqsImportFilterItem = ({ index, item, loading, onDelete, onUpdate }: FaqsImportFilterItemProps) => {
  const { t } = useTranslation();

  const { datasetId } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const form = useForm({
    initialValues: item,
    validate: {
      question: (value) => {
        return value ? null : t('fields.question_invalid');
      },
      answer: (value) => {
        return value ? null : t('fields.answer_invalid');
      },
    },
  });
  const handleRichEditorChange = useCallback(
    (value: string) => {
      form.setFieldValue('answer', value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.setFieldValue]
  );

  const handleCancel = () => {
    setIsEditing(false);
    form.reset();
  };

  const handleSubmit = (values: typeof form.values) => {
    setIsEditing(false);
    onUpdate(values);
  };

  const handleDelete = () => {
    setIsEditing(false);
    form.reset();
    onDelete(item);
  };

  return (
    <ContentWrap>
      <form className={classes.form} onSubmit={form.onSubmit(handleSubmit)}>
        <div
          className="flex justify-between items-center py-3 px-5 gap-3"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'nowrap',
          }}
          onClick={() => {
            if (!isEditing) setIsEditing(true);
          }}
        >
          <Group gap={4} className="flex-1 overflow-hidden" wrap="nowrap">
            <div className="bg-violet-500 rounded-full w-9 h-6 text-center text-white leading-6 flex-shrink-0 text-xs">
              {index + 1}
            </div>
            {isEditing ? (
              <Input.Wrapper id="form_question" label={`${t('faqs.form.question')}:`} className={classes.inputWrapper}>
                <Input
                  className="flex-1"
                  id="yoform_question"
                  placeholder={t('faqs.form.question_placeholder')}
                  {...form.getInputProps('question')}
                />
              </Input.Wrapper>
            ) : (
              <>
                <Text c="gray.8" className="flex-shrink-0" size="sm">
                  {t('faqs.form.question')}:
                </Text>
                <Text size="sm" className="truncate">
                  {item.question}
                </Text>
              </>
            )}
          </Group>
          <div>
            <ActionIcon
              variant="subtle"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(!isEditing);
              }}
            >
              {isEditing ? <SvgCollapse /> : <SvgExpand />}
            </ActionIcon>
          </div>
        </div>
        {isEditing && (
          <Stack className="mx-5 mb-2 px-10 py-5 bg-violet-100 rounded-lg">
            <TextInput
              label={`${t('faqs.form.source')}:`}
              placeholder={t('faqs.form.source_placeholder')}
              {...form.getInputProps('sources')}
            />
            <div className="text-left flex gap-1">
              <Stack gap="3" w={'100%'}>
                <label className="text-sm">{`${t('faqs.form.answer')}:`}</label>
                <ErrorBoundary fallback={() => <ErrorContent type="editor" />}>
                  <RichEditor
                    className={classes.editor}
                    datasetId={datasetId}
                    placeholder={t('faqs.form.answer_placeholder')}
                    defaultValue={item.answer ?? ''}
                    onChange={handleRichEditorChange}
                  />
                </ErrorBoundary>
              </Stack>
            </div>
            <div className="flex justify-between">
              <Button
                variant="light"
                color="red"
                className={cx(classes.removeButton, 'filled')}
                leftSection={<SvgTrash />}
                onClick={handleDelete}
                disabled={loading}
              >
                {t('delete_btn')}
              </Button>
              <Group>
                <Button variant="light" onClick={handleCancel}>
                  {t('alert_cancel')}
                </Button>
                <Button type="submit" disabled={loading}>
                  {t('save')}
                </Button>
              </Group>
            </div>
          </Stack>
        )}
      </form>
    </ContentWrap>
  );
};

export const FaqsImportFilter = () => {
  const value = useStepValue<{ faqs: FaqsItem[] } | undefined>();
  const [faqs, setFaqs] = useState<FaqsItem[]>((value?.faqs ?? []).map((item, index) => ({ ...item, id: `${index}` })));
  const navigate = useNavigate();

  const matchDatasetInBotPage = useMatch('/:bot_or_assistant/:botId/datasets/:datasetId/data-source/faq');
  const matchDatasetInDatasetPage = useMatch('/datasets/:datasetId/data-source/faq');
  const datasetId = matchDatasetInBotPage?.params?.datasetId || matchDatasetInDatasetPage?.params?.datasetId;

  const [importing, setImporting] = useState(false);
  const mode = useFaqsImportMode();
  const { t, i18n } = useTranslation();
  const { loadList } = useFAQsList(datasetId, { defaultPageSize: 50 });
  const { setCounter, counter } = useDataSourcesModal();

  const handleDelete = (item: FaqsItem) => {
    setFaqs((faqs) => {
      const _faqs = [...faqs];
      const index = _faqs.findIndex((i) => i.id === item.id);
      _faqs.splice(index, 1);
      return _faqs;
    });
  };

  const handleUpdate = (item: FaqsItem) => {
    setFaqs((faqs) => {
      const _faqs = [...faqs];
      const index = _faqs.findIndex((i) => i.id === item.id);
      _faqs[index] = item;
      return _faqs;
    });
  };

  const { batchAddFAQs } = useFAQsMethods(datasetId);
  const handleImport = () => {
    setImporting(true);
    batchAddFAQs({
      datasetId: datasetId,
      data: faqs.map(({ id, ...item }) => item),
    })
      .then(() => {
        setImporting(false);
        // 触发数据源页面刷新文件类型计数
        setCounter(counter + 1);
        if (mode === 'modal') {
          closeAllModals();
          loadList({ page: 1, language: i18n.language });
        } else navigate(`/datasets/${datasetId}/data-base`);
      })
      .catch((err) => {
        setImporting(false);
        showNotification({ message: err.message, color: 'red' });
        console.log('err...', err);
      });
  };

  const goHome = useFaqsImportHome();

  const handleRetrieve = () => {
    goHome();
  };

  return (
    <Stack gap="sm">
      <div className="flex justify-between flex-wrap gap-3 bg-violet-50 py-3 px-3 rounded">
        <Group gap="sm">
          <Text size="lg" fw={500} c="gray.8">
            {t('faqs.import.filter.title')}
          </Text>
        </Group>
        <Group wrap="nowrap">
          <span className="text-gray-500 font-light">
            {t('faqs.import.filter.extracted_count', { count: faqs.length })}
          </span>
          <Button variant="outline" className="filled" onClick={handleRetrieve} leftSection={<SvgSearch1 />}>
            {t('faqs.import.filter.retrieve')}
          </Button>
          <Button loading={importing} onClick={handleImport} leftSection={<SvgFile />}>
            {t('faqs.import.filter.import_library')}
          </Button>
        </Group>
      </div>
      {faqs.map((item, index) => (
        <FaqsImportFilterItem
          key={item.id}
          item={item}
          index={index}
          loading={importing}
          onDelete={handleDelete}
          onUpdate={handleUpdate}
        />
      ))}
    </Stack>
  );
};
